"use client";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/shadcn/dialog";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { joinGiveawayEvent } from "../../_actions/giveaway/join-giveaway-event";
import { GiveawayForm } from "./giveaway-form";
import type { GiveawayFormData } from "./types";

interface GiveawayRegistrationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    eventId: number;
    eventTitle: string;
}

export function GiveawayRegistrationModal({
    open,
    onOpenChange,
    eventId,
    eventTitle,
}: GiveawayRegistrationModalProps) {
    const t = useTranslations("events");

    const router = useRouter();

    const joinMutation = useMutation({
        mutationFn: (data: GiveawayFormData) =>
            joinGiveawayEvent(eventId, data),
        onSuccess: (result) => {
            if (!result.success) {
                toast.error(result.error?.message || t("applicationError"));
                return;
            }

            toast.success(result.message || t("applicationSuccess"));
            onOpenChange(false);
            router.refresh();
        },
        onError: (error) => {
            console.error("Form submission error:", error);
            toast.error(t("applicationErrorRetry"));
        },
    });

    const handleSubmit = async (data: GiveawayFormData) => {
        joinMutation.mutate(data);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t("giveawayRegistrationTitle")}</DialogTitle>
                    <DialogDescription>
                        {t("giveawayRegistrationBody")}
                    </DialogDescription>
                </DialogHeader>
                <GiveawayForm
                    eventTitle={eventTitle}
                    onSubmit={handleSubmit}
                    onBack={() => onOpenChange(false)}
                    isSubmitting={joinMutation.isPending}
                />
            </DialogContent>
        </Dialog>
    );
}
